import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, matchPath } from "react-router-dom";
import { lazy, Suspense, useMemo, useLayoutEffect } from "react";
import Index from "./pages/Index";
import GlobalControls, { GlobalControlsProvider } from "./components/GlobalControls";
import StaleCacheRescueDialog from "./components/StaleCacheRescueDialog";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { ScreenLoading } from "./components/ScreenLoading";
import { AmbientMusicProvider } from "./components/game/AmbientMusic";
import { CHRONICLE_AUDIO, readSavedOriginTrack, originTrackSrc } from "./components/game/ChronicleLayout";
import { AuthProvider } from "./hooks/useAuth";

const ChronicleDetail = lazy(() => import("./pages/ChronicleDetail"));
const Play = lazy(() => import("./pages/Play"));
const ProtagonistIntro = lazy(() => import("./pages/ProtagonistIntro"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const ChronicleLayout = lazy(() => import("./components/game/ChronicleLayout"));
const FeedbackAdmin = lazy(() => import("./pages/admin/FeedbackAdmin"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
import CloudSyncBridge from "./components/CloudSyncBridge";
import HowToPlayTutorial from "./components/HowToPlayTutorial";

// ═══════════════════════════════════════════════════════════════
// RouterAwareAmbientMusic — the App-level music provider.
// ═══════════════════════════════════════════════════════════════
//
// Sits ABOVE the <Routes> in the component tree, inside <BrowserRouter>
// so it can call useLocation(). Stays mounted across every route
// change in the app — Home → /chronicle/:id → /chronicle/:id/protagonist
// → /chronicle/:id/play — which means the audio element it owns
// never gets torn down by navigation.
//
// Pre-2026-05 this provider lived inside ChronicleLayout, and a
// chain of fragile invariants (inner Suspense placement, useMemo
// stability, primitive resolvedOrigin) was supposed to keep it
// from being remounted on navigation. Every few weeks one of those
// invariants would silently regress and the music would start
// resetting on every screen transition again. Moving it here makes
// that class of bug structurally impossible.
//
// Source resolution: parses the current pathname to extract the
// chronicle id (`/chronicle/:id/...`), looks up the chronicle's
// neutral track in CHRONICLE_AUDIO, then asks readSavedOriginTrack
// whether a per-origin override (e.g. Red vs White) applies. The
// resolved src is passed to AmbientMusicProvider; the provider's
// activeSrc useEffect handles the swap when src changes (and bails
// when src is undefined, e.g. on the home route).
function RouterAwareAmbientMusic({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const audioConfig = useMemo(() => {
    // Extract chronicle id from `/chronicle/:id` and its children.
    const match = matchPath({ path: "/chronicle/:id/*", end: false }, location.pathname);
    const chronicleId = match?.params?.id;
    if (!chronicleId) return { src: undefined, startOffset: 0 };
    const base = CHRONICLE_AUDIO[chronicleId];
    if (!base) return { src: undefined, startOffset: 0 };
    // SCREEN-BASED rule — the single source of truth for which track plays:
    //   • the in-game /play screen ALWAYS gets the per-origin track;
    //   • every pre-selection screen (the chronicle overview and the
    //     protagonist-select) ALWAYS gets the neutral base.
    // Deciding it here from the ROUTE — not from whether a save merely
    // exists — means leaving /play for any other screen flips back to
    // neutral, and entering /play from any screen (fresh, Continue, or a
    // replay) flips to the origin track. Play.tsx's switchTrack still runs
    // to cover an origin picked as an in-story first choice (no ?origin= in
    // the URL yet), but it is no longer what the story track depends on.
    const onPlay = !!matchPath({ path: "/chronicle/:id/play", end: true }, location.pathname);
    if (onPlay) {
      // Prefer the URL ?origin= (a fresh play launched from a protagonist
      // card), then the saved origin flag (the Continue path). Either
      // resolves to the per-origin track; if neither is set yet, fall back
      // to the neutral base and let Play.tsx swap once the origin is known.
      const urlOrigin = new URLSearchParams(location.search).get("origin");
      const originSrc = originTrackSrc(chronicleId, urlOrigin) ?? readSavedOriginTrack(chronicleId);
      if (originSrc) return { src: originSrc, startOffset: base.startOffset ?? 0 };
    }
    return { src: base.src, startOffset: base.startOffset ?? 0 };
  }, [location.pathname, location.search]);

  return (
    <AmbientMusicProvider src={audioConfig.src} startOffset={audioConfig.startOffset}>
      {children}
    </AmbientMusicProvider>
  );
}

// ═══════════════════════════════════════════════════════════════
// ScrollToTop — reset scroll position on every route change.
// ═══════════════════════════════════════════════════════════════
//
// React Router keeps the previous route's scroll position across a
// client navigation (the document is never reloaded). On phones the
// chronicle overview, the protagonist-select, and the in-game screens
// are all taller than the viewport and scroll the document body, so
// entering a new screen after scrolling the previous one left the
// player part-way down the page. Desktop happened to be fine only
// because those screens usually fit without scrolling.
//
// This resets the document scroll to the top whenever the pathname
// changes, so every screen opens at the top on every device.
// history.scrollRestoration = "manual" stops the browser from
// re-applying its own remembered position on back/forward and fighting
// the reset. useLayoutEffect runs before paint so there's no visible
// jump on the one eagerly-loaded route (the home page); the lazy
// chronicle routes reset during their Suspense fallback, before their
// content paints.
function ScrollToTop() {
  const { pathname } = useLocation();
  useLayoutEffect(() => {
    if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

const queryClient = new QueryClient();

// Run progression validation in dev mode
if (typeof window !== "undefined" && (import.meta.env.DEV || new URLSearchParams(window.location.search).has("dev"))) {
  // Loaded on demand: the validator reads every chronicle's full memory-flag
  // text, which must stay out of the first-load bundle.
  void import("./engine/progressionValidator").then((m) => m.logProgressionValidation());
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <GlobalControlsProvider>
          <BrowserRouter>
            <ScrollToTop />
            <GlobalControls />
            <CloudSyncBridge />
            {/* RouterAwareAmbientMusic wraps the entire route tree so the
                AmbientMusicProvider stays mounted across every navigation
                — including the chronicle → protagonist intro → /play
                transitions that have repeatedly regressed when the
                provider lived inside ChronicleLayout. DO NOT move this
                wrapper inside the <Routes>; see the warning in
                src/components/game/AmbientMusic.tsx. */}
            <RouterAwareAmbientMusic>
              <HowToPlayTutorial />
              {/* ErrorBoundary catches any render-time error in the route
                  tree — lazy-chunk load failures, engine mis-wires, missing
                  modules — and shows a recovery card instead of leaving the
                  player on a blank page. ScreenLoading replaces the blank
                  fallback that the player used to see while a chunk was
                  fetching, so transitions read as "loading" instead of
                  "the game crashed". */}
              <ErrorBoundary label="route">
                <Suspense fallback={<ScreenLoading />}>
                  <Routes>
                    <Route path="/" element={<Index />} />
                    <Route path="/chronicle/:id" element={<ChronicleLayout />}>
                      <Route index element={<ChronicleDetail />} />
                      <Route path="protagonist" element={<ProtagonistIntro />} />
                      <Route path="play" element={<Play />} />
                    </Route>
                    <Route path="/privacy" element={<Privacy />} />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/admin/feedback" element={<FeedbackAdmin />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </ErrorBoundary>
            </RouterAwareAmbientMusic>
          </BrowserRouter>
          <StaleCacheRescueDialog />
        </GlobalControlsProvider>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
